import { SERVER_FEATURE_KEYS, useServerFeatureUsageFor, useUnlockServerFeature } from '@/services/featureUsage'
import { useCallback } from 'react'
import { useHasDuprPlus } from './useHasFeature'

/**
 * Whether the active user's profile views are limited.
 *
 * True only when a backend feature-quota rule grants them a capped
 * `profile-view` quota AND they are not DUPR+. Rollout is controlled by
 * admin-authored rules on the backend — an ungranted or unlimited account is
 * never gated. The DUPR+ check stays as a client belt so a mis-authored rule
 * can never lock subscribers out.
 */
export function useProfileViewsLimited(): boolean {
  const usage = useServerFeatureUsageFor(SERVER_FEATURE_KEYS.profileView)
  const isDuprPlus = useHasDuprPlus()
  return usage.hasGrant && !usage.isUnlimited && !isDuprPlus
}

export interface ProfileViewsUsage {
  /** Player ids already unlocked this window (viewing them again is free). */
  viewedIds: number[]
  viewsUsed: number
  viewsLimit: number
  viewsRemaining: number
  viewsLimitReached: boolean
  /** When the allotment next replenishes (epoch seconds); null when unknown. */
  resetAtEpochSeconds: number | null
  /** Spends one profile view on `profileId` (idempotent for held ids). */
  recordView: (profileId: number) => void
  isLoading: boolean
}

/**
 * The `profile-view` active-set quota mapped onto profile-domain names.
 * The server holds the unlocked set, exempts the caller's own profile, and
 * never re-charges a held id.
 */
export function useProfileViews(): ProfileViewsUsage {
  const usage = useServerFeatureUsageFor(SERVER_FEATURE_KEYS.profileView)
  const unlock = useUnlockServerFeature(SERVER_FEATURE_KEYS.profileView)
  const recordView = useCallback((profileId: number) => unlock(String(profileId)), [unlock])
  return {
    viewedIds: usage.heldResourceIds.map(Number),
    viewsUsed: usage.used,
    viewsLimit: usage.limit ?? 0,
    viewsRemaining: Number.isFinite(usage.remaining) ? usage.remaining : 0,
    viewsLimitReached: usage.limitReached,
    resetAtEpochSeconds: usage.resetAtEpochSeconds,
    recordView,
    isLoading: usage.isLoading,
  }
}
